import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// components
import { useSnackbar } from 'src/components/snackbar';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { useLocales } from 'src/locales';
import { useSelector, useDispatch } from 'react-redux';
import { selectFilteredData } from 'src/redux/selectors';
import { addIssuedCheck } from 'src/redux/slices/dataSlice';
import { getBeneficiairesFromChecks } from 'src/utils/checks/get-benefeceires';

interface CheckDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  ribId: number;
  carnetId: number;
}

const CarnetCheckDialog: React.FC<CheckDialogProps> = ({ open, onClose, companyId, ribId, carnetId }) => {
  const filteredData = useSelector(selectFilteredData);
  const [check, setCheck] = useState<string | null>('');
  const [beneficiary, setBeneficiary] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [takenDate, setTakenDate] = useState<Date | null>(new Date());
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [bill, setBill] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [checkOptions, setCheckOptions] = useState<(string | null)[]>([]);
  const { t } = useLocales();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { makeRequest } = useServerRequest();
  const listBenefecieries = getBeneficiairesFromChecks(filteredData);

  // Memoize the handleCheckbookChange function using useCallback
  const handleCheckbookChange = useCallback(
    (carnetIdTemp: number | null) => {
      // setCheck(null);

      if (carnetIdTemp && ribId !== null) {
        // Find the selected RIB within the filtered data
        const selectedRib = filteredData
          .find(comp => comp.id === companyId)
          ?.ribs.find(rb => rb.id === ribId);

        if (selectedRib) {
          const selectedCheckbook = selectedRib.carnets.find(cb => cb.id === carnetIdTemp);

          if (selectedCheckbook) {
            // Generate the list of check codes, excluding those that already exist
            const existingCheckCodes = new Set(selectedCheckbook.checks.map(chck => chck.code));

            const checks = Array.from({ length: selectedCheckbook.taille }, (_, i) => {
              const numericPart = parseInt(selectedCheckbook.code.replace(/^\D+/g, ''), 10);
              const textPart = selectedCheckbook.code.replace(/\d+$/, '');
              const generatedCheckCode = `${textPart}${numericPart + i}`;

              // Only include the generated check code if it doesn't already exist
              if (!existingCheckCodes.has(generatedCheckCode)) {
                return generatedCheckCode;
              }
              return null; // Skip if it exists
            }).filter(Boolean); // Remove null values

            setCheckOptions(checks);
          } else {
            setCheckOptions([]);
          }
        } else {
          setCheckOptions([]);
        }
      } else {
        setCheckOptions([]);
      }
    },
    [companyId, ribId, filteredData]
  );
  // Run the function on component load and when newValue changes
  useEffect(() => {
    handleCheckbookChange(carnetId);
  }, [carnetId, handleCheckbookChange]);

  const handleSubmit = async () => {
    const formattedTakenDate = takenDate ? format(takenDate, 'yyyy-MM-dd HH:mm:ss') : null;
    const formattedDueDate = dueDate ? format(dueDate, 'yyyy-MM-dd HH:mm:ss') : null;

    const data = {
      company_id: companyId,
      rib_id: ribId,
      carnet_id: carnetId,
      check_id: check,
      benificier_id: beneficiary,
      amount,
      taken_date: formattedTakenDate,
      due_date: formattedDueDate, // optional
      facture: bill,   // optional
      remarque: note,  // optional
    };

    if (!data.company_id) {
      enqueueSnackbar(t('common.input.company.error1'), { variant: 'error' });
      return;
    }
    if (!data.rib_id) {
      enqueueSnackbar(t('common.input.rib.error2'), { variant: 'error' });
      return;
    }
    if (!data.carnet_id) {
      enqueueSnackbar(t('common.input.carnet.error1'), { variant: 'error' });
      return;
    }
    if (!data.check_id) {
      enqueueSnackbar(t('common.input.check.error1'), { variant: 'error' });
      return;
    }
    if (!data.benificier_id) {
      enqueueSnackbar(t('common.input.benef.error1'), { variant: 'error' });
      return;
    }
    if (!data.amount || Number.isNaN(Number(data.amount))) {
      enqueueSnackbar(t('common.input.amount.error1'), { variant: 'error' });
      return;
    }
    if (!data.taken_date) {
      enqueueSnackbar(t('common.input.takenDate.error1'), { variant: 'error' });
      return;
    }

    const response = await makeRequest(`/saveCheck`, {
      method: 'POST',
      data,
    }, t('common.alert.check'));

    if (response.success) {
      dispatch(addIssuedCheck({
        companyId: data.company_id,
        ribId: data.rib_id,
        carnetId: data.carnet_id,
        check: response.data.check,
      }))
      onClose();
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const formattedValue = value.replace(',', '.');
    setAmount(formattedValue);
  };

  return (
    <Dialog fullWidth disableEscapeKeyDown maxWidth="xs" open={open} onClose={(_, reason) => reason !== 'backdropClick' && onClose()}>
      <DialogTitle>{t('m.t.check')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: '10px' }}>
          <Grid item xs={12}>
            <Autocomplete
              options={checkOptions}
              getOptionLabel={(option) => option || ''}
              value={check !== null ? check : null}
              // isOptionEqualToValue={(option, value) => option === value}
              onChange={(event, newValue) => setCheck(newValue || '')}
              renderInput={(params) => <TextField {...params} label="Select Check" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              freeSolo
              options={listBenefecieries}
              getOptionLabel={(option) => option}
              value={beneficiary || ''}
              onInputChange={(event, newValue) => setBeneficiary(newValue.toUpperCase())}
              renderInput={(params) => <TextField {...params} label={t('common.input.benef.label')} />}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('common.input.amount.label')}
              value={amount || ''}
              onChange={handleAmountChange}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label={t('common.input.takenDate.label')}
              value={takenDate || null}
              onChange={(newValue) => setTakenDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label={t('common.input.dueDate.label')}
              value={dueDate || null}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('common.input.bill.label')}
              value={bill || ''}
              onChange={(e) => setBill(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('common.input.note.label')}
              multiline
              rows={2}
              value={note || ''}
              onChange={(e) => setNote(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('common.button.action.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {t('common.button.action.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarnetCheckDialog;
